/* eslint-disable @typescript-eslint/brace-style */
import { useNavigate } from 'react-router-dom';
import { UserProfile } from 'api/account/models/UserProfile';
import { Paged } from 'api/common/types/Page';
import UsersService from 'api/users/UsersService';
import { UserDto } from 'api/users/models/UserDto';
import { UserSearchCriteria } from 'api/users/models/UserSearchCriteria';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import Logger from 'common/services/Logger';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import styles from './UsersScreen.module.scss';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import toast from 'react-hot-toast';
import { Button, Container, Dropdown, DropdownButton, Stack, Form, Card } from 'react-bootstrap';
import Loading from 'common/services/Loading';

type Props = {
};

const UsersScreen: React.FC<Props> = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [criteria, setCriteria] = useState<UserSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderBy: 'asc',
        orderColumn: 'u.real_name'
    });
    const [usersPage, setUsersPage] = useState<Paged<UserDto>>();
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasUsersWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['USERS_WRITE']);
    const [filters, setFilters] = useState({
        allIn: ''
    });
    const getData = async () => {
        try {
            Loading.show();
            const page = await UsersService.getList(criteria);
            setUsersPage(page);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get users list', error);
            toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
        }
    }

    const renderTableActionCell = (row: UserDto) => {
        return <DropdownButton id="dropdown-basic-button" title="..." size="sm" variant="outline-secondary">
            <Dropdown.Item onClick={() => navigate(`/security/users/details/${row.id}`)}>{t('common.details')}</Dropdown.Item>
            {hasUsersWritePolicy && <>
                <Dropdown.Item onClick={() => navigate(`/security/users/edit/${row.id}`)}>{t('common.edit')}</Dropdown.Item>
                <Dropdown.Item onClick={() => showRemoveItemDialog(row)}>{t('common.delete')}</Dropdown.Item>
            </>
            }
        </DropdownButton>
    };

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setCriteria({ ...criteria, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field });
    }

    const tableColumns: ListingTableColumn<UserDto>[] = [
        {
            field: 'realName',
            name: t('security.users.list.name') ?? '',
            onSearch: onTableFilter,
            searchField: 'u.realName'
        },
        {
            field: 'email',
            name: t('security.users.list.email') ?? '',
            onSearch: onTableFilter,
            searchField: 'u.email'
        },
        {
            name: '',
            width: '60px',
            cellAlignment: 'right',
            preventClick: true,
            renderCell: renderTableActionCell,
            cellStyle: { overflow: 'unset' }
        },
    ];

    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<UserDto | null>(null);

    const showRemoveItemDialog = async (item: UserDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onRowClick = (event: any, id?: string) => {
        if (id) {
            navigate(`/security/users/details/${id}`);
        }
    }

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            toast.error(t('messages.record_delete_error'));
            return;
        }
        try {
            await UsersService.remove(itemToRemove);
            onCancelRemove();
            toast.success(t('messages.record_delete_success'));
            setCriteria({ ...criteria, page: 1 })
        }
        catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete users', error);
            toast.error(t('messages.record_delete_error'));
        }
    };

    useEffect(() => {
        getData();
    }, [criteria]);

    function handleChange(e: any) {
        const key = e.target.name;
        const value = e.target.value;
        setFilters({ ...filters, [key]: value });
    }
    function clearFilters() {
        setFilters({ allIn: '' });
    }
    const onSearch = () => {
        setCriteria({ ...criteria, allIn: filters.allIn });
    }

    return (
        <ScreenTitle title={t('security.users.title')}>
            <Container fluid>
                <Card className={styles.filters}>
                    <Card.Body>
                        <Stack direction="horizontal" gap={3} >
                            <Form.Control type="text" value={filters.allIn} className="me-auto" name="allIn" onChange={handleChange} placeholder={t('common.write_here')} />
                            <Button variant="secondary" onClick={onSearch}>{t('common.search')}</Button>
                            <div className="vr" />
                            <Button variant="outline-danger" onClick={clearFilters}>{t('common.clear')}</Button>
                            <Button variant="primary" onClick={() => navigate('/security/users/new/')}>{t('common.new')}</Button>
                        </Stack>
                    </Card.Body>
                </Card>
            </Container>
            <Container fluid>
                <ListingTable
                    columns={tableColumns}
                    rows={usersPage?.items || []}
                    onRowClick={(row, _, event) => onRowClick(event, row.id)}
                    allowHover={true}
                    initialSearch={{ colField: 'u.real_name', isOrderAsc: true }}
                />
                <PaginationWithInfo
                    itemName={t('security.users.title')}
                    currentPage={usersPage?.currentPage ?? 1}
                    pageItems={usersPage?.items.length || 0}
                    totalItems={usersPage?.totalItems || 0}
                    onChange={page => setCriteria({ ...criteria, page: page })}
                />
            </Container>
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('messages.remove_record_with_ident', { name: itemToRemove?.realName ?? '' })} />
        </ScreenTitle>
    );
};

export default UsersScreen;
